import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint } from '@fortawesome/free-solid-svg-icons';
import '../module_mechanics/Styles/FormList.css';
import gatepassStamp from '../assets/images/gpass.png';

const API_URL = 'https://ourzestapp.online/oz_backend/solar_module/solar_gatepass.php';
const escapeHtml = value => String(value ?? '').replace(/[&<>'"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;' }[character]));
const useOurzestAppearance = (cssPrefix) => {
    const [themeColor, setThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [language, setLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const applyTheme = useCallback((value) => {
        const color = /^#[0-9a-f]{6}$/i.test(value || '') ? value : '#6366f1';
        const root = document.documentElement;
        root.style.setProperty(`--${cssPrefix}-primary`, color);
        root.style.setProperty(`--${cssPrefix}-primary-dark`, color);
        root.style.setProperty(`--${cssPrefix}-primary-light`, color);
        setThemeColor(color);
    }, [cssPrefix]);
    useEffect(() => {
        const sync = () => {
            applyTheme(localStorage.getItem('ourzest_theme') || '#6366f1');
            setDarkMode(localStorage.getItem('ourzest_darkMode') === 'true');
            setLanguage(localStorage.getItem('ourzest_language') || 'en');
        };
        const onStorage = event => ['ourzest_theme', 'ourzest_darkMode', 'ourzest_language'].includes(event.key) && sync();
        const onTheme = event => applyTheme(event.detail?.theme || localStorage.getItem('ourzest_theme') || '#6366f1');
        const onDark = event => setDarkMode(event.detail?.darkMode ?? localStorage.getItem('ourzest_darkMode') === 'true');
        const onLanguage = event => setLanguage(event.detail?.language || localStorage.getItem('ourzest_language') || 'en');
        sync();
        window.addEventListener('storage', onStorage);
        window.addEventListener('themeChanged', onTheme);
        window.addEventListener('darkModeToggled', onDark);
        window.addEventListener('languageChanged', onLanguage);
        const timer = window.setInterval(sync, 1000);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('themeChanged', onTheme);
            window.removeEventListener('darkModeToggled', onDark);
            window.removeEventListener('languageChanged', onLanguage);
            window.clearInterval(timer);
        };
    }, [applyTheme]);
    useEffect(() => {
        document.body.classList.toggle(`${cssPrefix}-dark`, darkMode);
        document.documentElement.classList.toggle(`${cssPrefix}-dark`, darkMode);
    }, [cssPrefix, darkMode]);
    return { themeColor, darkMode, language, isRTL: language === 'ur' || language === 'ar' };
};
const request = async payload => {
    const response = await fetch(API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    const data = await response.json();
    if (!response.ok || data.status !== 'success') throw new Error(data.message || 'Request failed');
    return data;
};

export default function SolarGatepassList() {
    const { user, currentShop } = useAuth();
    const [rows, setRows] = useState([]);
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    const [editProductSearch, setEditProductSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);
    const [notice, setNotice] = useState(null);
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '', businessaddress: '', phonenumber: '',
        bussinesslogo: '', bussinesslogo_url: ''
    });
    const { darkMode, isRTL } = useOurzestAppearance('ozlistui');

    const fetchBusinessInfo = useCallback(async () => {
        const fallback = {
            businessname: user?.business_name || user?.fullname || currentShop?.shop_name || 'OurZest Store',
            businessaddress: user?.business_address || '',
            phonenumber: user?.phonenumber || user?.phone || '',
            bussinesslogo: '', bussinesslogo_url: ''
        };
        if (!user?.username) { setBusinessInfo(fallback); return fallback; }
        try {
            const response = await fetch(`https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=${encodeURIComponent(user.username)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok || data.status !== 'success') throw new Error(data.message || 'Business information unavailable');
            const info = {
                businessname: data.businessname || fallback.businessname,
                businessaddress: data.businessaddress || fallback.businessaddress,
                phonenumber: data.phonenumber || fallback.phonenumber,
                bussinesslogo: data.bussinesslogo || '',
                bussinesslogo_url: data.bussinesslogo_url || ''
            };
            setBusinessInfo(info);
            return info;
        } catch (error) {
            console.warn('Using fallback business information:', error);
            setBusinessInfo(fallback);
            return fallback;
        }
    }, [currentShop?.shop_name, user]);

    const load = useCallback(async () => {
        if (!currentShop?.shop_id) { setRows([]); return; }
        setLoading(true);
        try {
            const [listData, productData] = await Promise.all([
                request({ action: 'list', shop_id: currentShop.shop_id, username: user?.username || '' }),
                request({ action: 'get_products', shop_id: currentShop.shop_id, username: user?.username || '' })
            ]);
            setRows(listData.gatepasses || []);
            setProducts((productData.products || []).map(p => ({ ...p, id: Number(p.id) })));
        } catch (error) { setNotice({ type: 'error', text: error.message }); }
        finally { setLoading(false); }
    }, [currentShop?.shop_id, user?.username]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { fetchBusinessInfo(); }, [fetchBusinessInfo]);

    const visibleRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter(row => [row.serial_number, row.party_name, row.driver_name, row.driver_phone, row.vehicle_no, row.checked_by, row.approved_by, ...(row.products || []).map(p => p.product_name)].some(value => String(value || '').toLowerCase().includes(term)));
    }, [rows, search]);

    const openEdit = row => {
        setEditProductSearch('');
        setEditing({
            ...row,
            selected_products: (row.products || []).map(p => Number(p.product_id)),
            total_quantity: row.total_quantity ?? '', total_bundle: row.total_bundle ?? ''
        });
    };

    const updateEdit = (field, value) => setEditing(prev => ({ ...prev, [field]: value }));
    const toggleEditProduct = id => setEditing(prev => {
        const exists = prev.selected_products.includes(id);
        const selected_products = prev.selection_mode === 'single' ? [id] : exists ? prev.selected_products.filter(v => v !== id) : [...prev.selected_products, id];
        return { ...prev, selected_products };
    });

    const filteredEditProducts = useMemo(() => {
        const term = editProductSearch.trim().toLowerCase();
        return term ? products.filter(product => product.product_name?.toLowerCase().includes(term)) : products;
    }, [editProductSearch, products]);

    const saveEdit = async event => {
        event.preventDefault();
        if (!editing.selected_products.length) { setNotice({ type: 'error', text: 'Select at least one product' }); return; }
        setSaving(true);
        try {
            await request({
                action: 'update', gatepass_id: editing.gatepass_id, shop_id: currentShop.shop_id,
                party_name: currentShop.shop_name || editing.party_name, username: user?.username || '',
                driver_name: editing.driver_name, driver_phone: editing.driver_phone, vehicle_no: editing.vehicle_no,
                selection_mode: editing.selection_mode, selected_products: editing.selected_products,
                total_quantity: Number(editing.total_quantity || 0), total_bundle: Number(editing.total_bundle || 0),
                checked_by: editing.checked_by, approved_by: editing.approved_by
            });
            setEditing(null); setNotice({ type: 'success', text: 'Gate pass updated successfully' }); await load();
        } catch (error) { setNotice({ type: 'error', text: error.message }); }
        finally { setSaving(false); }
    };

    const remove = async row => {
        if (!window.confirm(`Delete gate pass ${row.serial_number}?`)) return;
        try {
            await request({ action: 'delete', gatepass_id: row.gatepass_id, shop_id: currentShop.shop_id, username: user?.username || '' });
            setNotice({ type: 'success', text: 'Gate pass deleted' }); await load();
        } catch (error) { setNotice({ type: 'error', text: error.message }); }
    };

  const printGatepass = async row => {
        const printWindow = window.open('', '_blank', 'width=420,height=720');
        if (!printWindow) {
            setNotice({ type: 'error', text: 'Please allow pop-ups to print the gate pass' });
            return;
        }
        
        // Fetch fresh business data to ensure we have the address
        let businessData = businessInfo.businessname ? businessInfo : await fetchBusinessInfo();
        
        // If businessData still doesn't have address, try to fetch directly from API
        if (!businessData.businessaddress && user?.username) {
            try {
                const response = await fetch(`https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=${encodeURIComponent(user.username)}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    credentials: 'include'
                });
                const data = await response.json();
                if (data.status === 'success' && data.businessaddress) {
                    businessData = data;
                }
            } catch (error) {
                console.warn('Could not fetch business address:', error);
            }
        }
        
        const businessName = businessData.businessname || currentShop?.shop_name || 'OurZest Store';
        const businessAddress = businessData.businessaddress || user?.business_address || '';
        const businessPhone = businessData.phonenumber || user?.phonenumber || '';
        const rawLogo = businessData.bussinesslogo_url || businessData.bussinesslogo || '';
        const businessLogo = rawLogo && !/^(https?:|data:|blob:)/i.test(rawLogo)
            ? `https://ourzestapp.online/oz_backend/${String(rawLogo).replace(/^\/+/, '')}`
            : rawLogo;
            
        // Debug: Log what we have
        console.log('Business Data:', { businessName, businessAddress, businessPhone, businessLogo });
            
        const productNames = (row.products || []).map(product => product.product_name).filter(Boolean);
        
        // Modified detail rows - removed Checked By
        const detailRows = [
            ['Party Name', row.party_name],
            ['Driver Name', row.driver_name],
            ['Driver Ph No', row.driver_phone || '-'],
            ['Vehicle No', row.vehicle_no],
            ['Supply', row.selection_mode === 'single' ? 'Single Product' : 'Multiple Products'],
            ['Total Quantity', Number(row.total_quantity || 0).toLocaleString()],
            ['Total Bundle', Number(row.total_bundle || 0).toLocaleString()],
           
            ['Created By', row.username || '-'],
            ['Date / Time', row.created_at ? new Date(row.created_at).toLocaleString() : '-']
        ];
        const rowsHtml = detailRows.map(([label, value]) => `<div class="detail"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`).join('');
        
        // Increased product font size from 14px to 15px (1px increase)
        const productsHtml = productNames.length
            ? productNames.map((name, index) => `<div class="product"><b>${index + 1}.</b> ${escapeHtml(name)}</div>`).join('')
            : '<div class="product">No products</div>';

        printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(row.serial_number)}</title><style>
            @page { size: 76mm auto; margin: 3mm; }
            * { box-sizing: border-box; }
            html, body { width: 70mm; margin: 0; padding: 0; color: #000; background: #fff; }
            body { font-family: Arial, Helvetica, sans-serif; font-size: 13px; font-weight: 700; line-height: 1.28; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .receipt { width: 70mm; padding: 1mm; }
            .business-header { padding-bottom: 6px; margin-bottom: 6px; border-bottom: 3px double #000; text-align: center; }
            .business-logo { display: block; max-width: 27mm; max-height: 20mm; width: auto; height: auto; margin: 0 auto 3px; object-fit: contain; filter: grayscale(1) contrast(1.35); }
            .business-name { margin: 0; font-size: 19px; font-weight: 900; line-height: 1.15; text-transform: uppercase; overflow-wrap: anywhere; }
            /* Phone number - below business name */
            .business-phone { margin-top: 2px; font-size: 11px; font-weight: 700; line-height: 1.25; overflow-wrap: anywhere; color: #000; }
            /* Address - below phone number */
            .business-address { margin-top: 2px; font-size: 11px; font-weight: 700; line-height: 1.25; overflow-wrap: anywhere; color: #333; }
            .gatepass-title-row { display: flex; align-items: center; justify-content: center; width: 100%; min-height: 27mm; margin: 3px 0 4px; overflow: visible; }
            .serial { margin: 5px 0 8px; padding: 7px 3px; border: 2px solid #000; text-align: center; font-size: 17px; font-weight: 900; }
            .detail { display: grid; grid-template-columns: 42% 58%; gap: 3px; padding: 5px 1px; border-bottom: 1.5px dashed #000; }
            .detail span, .detail strong { font-weight: 900; overflow-wrap: anywhere; }
            .detail strong { text-align: right; }
            .products-section { margin-top: 12px; }
            /* Increased product font size from 14px to 15px */
            .product { padding: 5px 2px; border-bottom: 1.5px solid #000; font-size: 15px; font-weight: 900; overflow-wrap: anywhere; }
            .stamp-wrap { display: flex; align-items: center; justify-content: center; width: 38mm; height: 26mm; overflow: visible; }
            .gatepass-stamp { display: block; width: 35mm; max-width: 35mm; max-height: 25mm; object-fit: contain; opacity: .92; transform: rotate(-10deg); transform-origin: center; filter: grayscale(1) contrast(1.65); mix-blend-mode: multiply; }
            
            /* Signature section */
            .signature-section {
                margin-top: 15px;
                padding-top: 10px;
                border-top: 2px double #000;
                display: flex;
                justify-content: flex-end;
                padding-right: 10px;
            }
            .signature-box {
                text-align: center;
                width: 120px;
            }
            .signature-line {
                border-bottom: 1.5px solid #000;
                padding-bottom: 3px;
                margin-bottom: 3px;
                height: 25px;
            }
            .signature-label {
                font-size: 11px;
                font-weight: 900;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }
            
            .footer { margin-top: 9px; padding-top: 5px; border-top: 2px double #000; text-align: center; font-size: 11px; font-weight: 900; }
            @media print { html, body, .receipt { width: 70mm; } }
        </style></head><body><main class="receipt">
            <header class="business-header">
                ${businessLogo ? `<img src="${escapeHtml(businessLogo)}" alt="Business Logo" class="business-logo" onerror="this.style.display='none'" />` : ''}
                <div class="business-name">${escapeHtml(businessName)}</div>
                <!-- Phone number BELOW business name -->
                ${businessPhone ? `<div class="business-phone">Ph. ${escapeHtml(businessPhone)}</div>` : ''}
                <!-- Address BELOW phone number -->
                ${businessAddress ? `<div class="business-address">Add: ${escapeHtml(businessAddress)}</div>` : ''}
                <!-- DEBUG: Show if address is missing -->
                ${!businessAddress ? `<div style="font-size:8px;color:#999;margin-top:2px;">(No address available)</div>` : ''}
            </header>
            <div class="gatepass-title-row"><div class="stamp-wrap"><img src="${escapeHtml(gatepassStamp)}" alt="Gate Pass Stamp" class="gatepass-stamp" onerror="this.style.display='none'" /></div></div>
            <div class="serial">${escapeHtml(row.serial_number)}</div>
            ${rowsHtml}
            <div class="products-section">${productsHtml}</div>
            
            <!-- Signature section with "Approved by" label below the line -->
            <div class="signature-section">
                <div class="signature-box">
                    <div class="signature-line">&nbsp;</div>
                    <div class="signature-label">Approved by</div>
                </div>
            </div>
            
            <div class="footer">*** Powered by OurZest PVT LTD ***</div>
        </main><script>window.addEventListener('load',function(){setTimeout(function(){window.print();},250);});<\/script></body></html>`);
        printWindow.document.close();
        printWindow.focus();
    };

    return (
        <div className={`ozlistui-container ${isRTL ? 'ozlistui-rtl' : 'ozlistui-ltr'} ${darkMode ? 'ozlistui-dark' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="ozlistui-sticky-header">
                <div className="ozlistui-header-bg-animation"><div className="gradient-orb orb-1"></div><div className="gradient-orb orb-2"></div><div className="gradient-orb orb-3"></div></div>
                <div className="ozlistui-header-content"><div className="ozlistui-header-left"><div className="ozlistui-header-title"><h2>Gate Pass List</h2><p>{currentShop?.shop_name || 'Select a shop'}</p></div></div><div className="ozlistui-header-right"><div className="ozlistui-header-search-wrapper"><div className="ozlistui-floating-wrapper ozlistui-header-search"><input className="ozlistui-header-search-input" type="search" placeholder="Search serial, driver, vehicle or product..." value={search} onChange={e => setSearch(e.target.value)} /></div></div><button className="ozlistui-refresh-btn" onClick={load}>Refresh</button></div></div>
            </div>
            {notice && <div className={`ozui_toast-notification ${notice.type}`}><div className="ozui_toast-content"><div className="ozui_toast-text"><div className="ozui_toast-title">{notice.type === 'success' ? 'Success' : 'Error'}</div><div className="ozui_toast-message">{notice.text}</div></div></div></div>}

            <div className="ozlistui-table-scroll"><div className="ozlistui-table-container"><div className="ozlistui-table-wrapper">
                <table className="ozlistui-table">
                    <thead><tr><th className="ozlistui-id-column ozlistui-centered-cell">#</th>{['Serial Number', 'Party Name', 'Driver Name', 'Driver Ph No', 'Vehicle No', 'Product Name', 'Total Quantity', 'Total Bundle', 'Checked By', 'Approved By', 'Date'].map(title => <th key={title} className="ozlistui-centered-cell">{title}</th>)}<th className="ozlistui-actions-column ozlistui-centered-cell">Actions</th></tr></thead>
                    <tbody>{loading ? <tr><td colSpan="13" className="ozlistui-loading-container">Loading gate passes...</td></tr> : visibleRows.length ? visibleRows.map((row, index) => <tr key={row.gatepass_id}><td className="ozlistui-id-cell ozlistui-centered-cell"><span className="ozlistui-cell-text">{index + 1}</span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text"><strong>{row.serial_number}</strong></span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{row.party_name}</span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{row.driver_name}</span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{row.driver_phone || '-'}</span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{row.vehicle_no}</span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{(row.products || []).map(p => p.product_name).join(', ') || '-'}</span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{Number(row.total_quantity || 0).toLocaleString()}</span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{Number(row.total_bundle || 0).toLocaleString()}</span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{row.checked_by}</span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{row.approved_by}</span></td><td className="ozlistui-centered-cell"><span className="ozlistui-cell-text">{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</span></td><td className="ozlistui-actions-cell ozlistui-centered-cell"><div className="ozlistui-actions-content"><button className="ozlistui-action-btn ozlistui-edit-btn" onClick={() => openEdit(row)} title="Edit Gate Pass" aria-label="Edit Gate Pass"><FontAwesomeIcon icon={faEdit} /></button><button className="ozlistui-action-btn ozlistui-view-btn" onClick={() => printGatepass(row)} title="Print on 3-inch thermal printer" aria-label="Print Gate Pass"><FontAwesomeIcon icon={faPrint} /></button><button className="ozlistui-action-btn ozlistui-delete-btn" onClick={() => remove(row)} title="Delete Gate Pass" aria-label="Delete Gate Pass"><FontAwesomeIcon icon={faTrash} /></button></div></td></tr>) : <tr><td colSpan="13" className="ozlistui-no-shop-container">No gate passes found</td></tr>}</tbody>
                </table>
            </div></div></div>

            {editing && <div className="ozlistui-modal-overlay" onMouseDown={e => e.target === e.currentTarget && setEditing(null)}><div className="ozlistui-modal-content ozlistui-edit-modal-content"><div className="ozlistui-modal-header"><div className="ozlistui-modal-title"><h3>Edit Gate Pass</h3><span className="ozlistui-product-number-badge">{editing.serial_number}</span></div><button className="ozlistui-close-btn" onClick={() => setEditing(null)}>×</button></div><form onSubmit={saveEdit}><div className="ozlistui-modal-body"><div className="ozlistui-form-grid">
                {[["party_name", "Party Name", currentShop?.shop_name || editing.party_name, true], ["driver_name", "Driver Name *", editing.driver_name, false], ["driver_phone", "Driver Ph No", editing.driver_phone || '', false], ["vehicle_no", "Vehicle No *", editing.vehicle_no, false], ["total_quantity", "Total Quantity", editing.total_quantity, false], ["total_bundle", "Total Bundle", editing.total_bundle, false], ["checked_by", "Checked By *", editing.checked_by, false], ["approved_by", "Approved By *", editing.approved_by, false]].map(([name, label, value, readOnly]) => <div className="ozlistui-form-group" key={name}><div className="ozlistui-input-wrapper"><input id={`edit_${name}`} type={name.startsWith('total_') ? 'number' : 'text'} step={name.startsWith('total_') ? '0.001' : undefined} min={name.startsWith('total_') ? '0' : undefined} required={['driver_name', 'vehicle_no', 'checked_by', 'approved_by'].includes(name)} readOnly={readOnly} value={value} placeholder=" " onChange={e => updateEdit(name, e.target.value)} /><label htmlFor={`edit_${name}`}>{label}</label></div></div>)}
                <div className="ozlistui-form-group"><div className="ozlistui-input-wrapper"><select id="edit_selection_mode" value={editing.selection_mode} onChange={e => setEditing(prev => ({ ...prev, selection_mode: e.target.value, selected_products: e.target.value === 'single' ? prev.selected_products.slice(0, 1) : prev.selected_products }))}><option value="single">Single Product</option><option value="multiple">Multiple Products</option></select><label htmlFor="edit_selection_mode">Selection Mode</label></div></div>
                <div className={`ozlistui-form-group ozlistui-full-width ${darkMode ? 'ozui-dark' : ''} ${isRTL ? 'ozui-rtl' : 'ozui-ltr'}`}>
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                id="edit_products_search"
                                type="text"
                                value={editProductSearch}
                                onChange={event => setEditProductSearch(event.target.value)}
                                placeholder=" "
                                autoComplete="off"
                            />
                            <label htmlFor="edit_products_search">
                                Search Product Name ({editing.selected_products.length} selected)
                            </label>
                        </div>
                        <div
                            className="ozui-category-dropdown"
                            style={{
                                position: 'relative', display: 'block', maxHeight: '245px',
                                overflowY: 'auto', padding: '6px', borderRadius: '10px',
                                direction: 'ltr', textAlign: 'left'
                            }}
                        >
                            {filteredEditProducts.length ? filteredEditProducts.map(product => (
                                <div
                                    key={product.id}
                                    className={`ozui-category-option ${editing.selected_products.includes(product.id) ? 'ozui-selected' : ''}`}
                                    onClick={() => toggleEditProduct(product.id)}
                                    style={{
                                        display: 'flex', flexDirection: 'row', alignItems: 'center',
                                        justifyContent: 'flex-start', direction: 'ltr', textAlign: 'left',
                                        gap: '10px', minHeight: '38px', padding: '7px 10px',
                                        margin: '2px 0', borderRadius: '7px', cursor: 'pointer',
                                        lineHeight: '1.2'
                                    }}
                                >
                                    <input
                                        type={editing.selection_mode === 'single' ? 'radio' : 'checkbox'}
                                        name="edit-gatepass-product"
                                        checked={editing.selected_products.includes(product.id)}
                                        readOnly
                                        style={{
                                            order: 0, width: '16px', height: '16px', minWidth: '16px',
                                            margin: 0, padding: 0, flex: '0 0 16px', cursor: 'pointer',
                                            accentColor: 'var(--ozui-primary, #6366f1)'
                                        }}
                                    />
                                    <span style={{ order: 1, flex: '1 1 auto', fontSize: '0.86rem', fontWeight: editing.selected_products.includes(product.id) ? 700 : 500, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{product.product_name}</span>
                                </div>
                            )) : <div className="ozui-category-empty">No products found</div>}
                        </div>
                    </div>
                </div>
            </div></div><div className="ozlistui-modal-footer"><button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setEditing(null)}>Cancel</button><button className="ozlistui-btn ozlistui-btn-primary" disabled={saving}>{saving ? 'Updating...' : 'Save Changes'}</button></div></form></div></div>}
        </div>
    );
}
